import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { session, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <FullscreenMessage message="Checking session..." />;
    }

    if (!session) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};

export const RequireOnboarded: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const { bootstrap, loading, error, refresh } = useAppBootstrap();

    if (loading) {
        return <FullscreenMessage message="Loading workspace..." />;
    }

    if (error && !bootstrap) {
        return (
            <div className="min-h-screen bg-theme text-theme flex flex-col items-center justify-center gap-4 px-6">
                <p className="text-sm font-medium text-theme-secondary text-center max-w-md">
                    Could not load your workspace.
                </p>
                <p className="text-xs text-theme-secondary/80 text-center max-w-lg whitespace-pre-wrap">
                    {error}
                </p>
                <button
                    type="button"
                    className="text-sm px-4 py-2 rounded-md border border-theme-secondary/40 text-theme-secondary hover:bg-theme-secondary/10"
                    onClick={() => void refresh()}
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!bootstrap) {
        return <FullscreenMessage message="Preparing workspace..." />;
    }

    if (bootstrap.needs_onboarding) {
        return <Navigate to={{ pathname: '/onboarding', search: location.search }} replace />;
    }

    return <>{children}</>;
};

const FullscreenMessage = ({ message }: { message: string }) => (
    <div className="min-h-screen bg-theme text-theme flex items-center justify-center">
        <div className="text-sm text-theme-secondary">{message}</div>
    </div>
);
